import { ElForm } from 'element-ui/types/form'
import { HttpRequestOptions } from 'element-ui/types/upload'
import { post, get } from '@/api/exam'
import { post as postRoom } from '@/api/exam/room'
import { post as postExaminees, patch } from '@/api/exam/examinees'
import { post as postInvigilator } from '@/api/exam/invigilator'
import { postDoc as postInvigilatorDoc } from '@/api/exam/invigilator'
import { alert, notify } from '@/utils/notification'
import { createProcessHandler } from '@/utils/upload'
import { Exam, UploadFunc } from '@/models/exam'
import { Nilable } from '@/models/helpers/common'
import { router } from '@/router'
import { toDateString } from '@/utils/formatter/date'

export type Subject = { name: string; time: Date }

export type Payload = {
  name: string
  time: Date
  subjects: Subject[]
}

const QUERY_EXAM_ID = 'exam_id'

const formater = new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  second: 'numeric',
  hour12: false
})

export async function validForm(el: ElForm) {
  try {
    await el.validate()
    return true
  } catch (e) {
    return false
  }
}

export async function createExam({ name, time, subjects }: Payload) {
  try {
    const exam = await post({
      extraParam: '',
      name,
      time: toDateString(time),
      subjects: subjects.map(a => ({
        name: a.name,
        time: toDateString(a.time)
      }))
    })
    notify(`创建考试成功`)
    router.push({ path: '/exam/upload', query: { [QUERY_EXAM_ID]: exam.id } })
    return true
  } catch (e) {
    return false
  }
}

const curryUpload = (upload: UploadFunc) => (id: string) => async (
  option: HttpRequestOptions
) => {
  try {
    const { count } = await upload(
      id,
      option.file,
      createProcessHandler(option)
    )
    notify(`上传成功, 共${count}条数据`)
    return true
  } catch (e) {
    notify('上传失败', e)
    return false
  }
}

/**
 * 上传考场信息
 * @param id 考试ID
 */
export const uploadRoom = curryUpload(postRoom)

/**
 * 上传考生信息
 * @param id 考试ID
 */
export const uploadExaminee = curryUpload(postExaminees)

/**
 * 上传监考信息
 * @param id 考试ID
 */
export const uploadInvigilator = curryUpload(postInvigilator)

/**
 * 上传监考通知模板
 * @param id 考试ID
 */
export const uploadInvigilatorDoc = curryUpload(postInvigilatorDoc)

export async function fetchExamList() {
  try {
    const data = await get({ current: 1, pageSize: 99999 })
    const rows = (data && data.rows) || []
    const formated = rows.map(a => ({
      ...a,
      time: new Date(a.time).toDateString()
    }))
    return formated
  } catch (e) {
    notify(`获取考试列表失败`, e)
    return []
  }
}

/** 异步校验 URL QUERY 中是否存在有效 ExamId */
export async function validExamByRoute(): Promise<string | null>
/** 异步校验 URL QUERY 中 ExamId 是否位于数组中 */
export async function validExamByRoute(exams?: Exam[]): Promise<string | null>
export async function validExamByRoute(exams?: Exam[]) {
  const exam = router.currentRoute.query[QUERY_EXAM_ID]
  if (typeof exam !== 'string') return null

  const result = exams
    ? exams.map(a => a.id).includes(exam)
    : await validExamAsync(exam)
  return result ? exam : null
}

async function validExamAsync(examId: string) {
  try {
    await get(examId)
    return true
  } catch (e) {
    return false
  }
}

export function validExam(examId?: Nilable<string>): examId is string {
  if (!examId) alert(`请先选择考试`, new Error())
  return !!examId
}

export async function validNotifyExam(examId: string) {
  if (!examId) {
    alert(`请先选择考试`, new Error())
    return false
  }
  var exam = await get(examId as string)
  if (!exam.extraParam) {
    alert(`所选考试未上传通知模板`, new Error())
    return false
  }
  return true
}

export async function start(examId?: Nilable<string>) {
  if (!validExam(examId)) return

  try {
    await patch(examId)
    router.push({
      path: '/print/exam-download',
      query: { [QUERY_EXAM_ID]: examId }
    })
    notify('排考完成')
    return true
  } catch (e) {
    notify(`排考可能失败, 如果多次失败, 请联系管理员`, e)
    return false
  }
}
